import React, { createContext, useState, useEffect, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { authAPI } from '../services/api'
import {
  fetchRuntimeConfig,
  createLoginRequest,
  createTokenRequest,
  isDevelopmentAuth
} from '../config/runtimeConfig'
import { isInIframe, notifyAuthSuccess, notifyAuthFailed, notifyAuthRequired } from '../utils/notifyParent'

const USER_STORAGE_KEY = 'agentflow_user_data'
const PARENT_TOKEN_KEY = 'agentflow_parent_token'
const PARENT_AUTH_TIMEOUT_MS = 30000
const PREAUTH_CACHE_KEY = 'agentflow_preauth_cache'
const PREAUTH_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export const AuthContext = createContext(null)

/**
 * AuthStateProvider holds all authentication state and logic.
 *
 * It is rendered as a singleton at the top of the tree (inside or outside
 * MsalProvider depending on the auth mode), so every consumer of useAuth()
 * shares the same state.  Previously, useAuth() was a plain custom hook and
 * each call site (App, LoginPage, …) got its own independent state, causing
 * concurrent validateToken() calls that raced against MSAL's interaction lock.
 */
export const AuthStateProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const stored = sessionStorage.getItem(USER_STORAGE_KEY)
    return stored ? true : false
  })
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState(() => {
    const stored = sessionStorage.getItem(USER_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  })
  const [runtimeConfig, setRuntimeConfig] = useState(null)
  const [authError, setAuthError] = useState(null)

  const hasValidated = useRef(false)
  const isValidating = useRef(false)
  const parentTokenTimeoutRef = useRef(null)

  // useMsal() must always be called (Rules of Hooks).
  // In dev auth mode there is no MsalProvider, so this returns the default
  // MSAL context (inProgress: 'startup', accounts: [], instance: undefined).
  // shouldUseMsal guards every code path that touches the MSAL instance.
  const msalContext = useMsal()
  const { instance, accounts, inProgress } = msalContext || {}

  const shouldUseMsal = runtimeConfig && !isDevelopmentAuth(runtimeConfig)

  const setUserAndPersist = (userData) => {
    setUser(userData)
    if (userData) {
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData))
    } else {
      sessionStorage.removeItem(USER_STORAGE_KEY)
    }
  }

  useEffect(() => {
    // Always fetch runtime config first, without blocking on MSAL inProgress.
    // In dev auth mode there is no MsalProvider so inProgress stays 'startup'
    // forever — blocking here would prevent auth from ever running.
    if (!runtimeConfig) {
      const initializeAuth = async () => {
        try {
          const config = await fetchRuntimeConfig()
          setRuntimeConfig(config)
          // Auth check runs on the next effect invocation triggered by runtimeConfig state change
        } catch (error) {
          console.error('Failed to initialize auth with runtime config:', error)
          setIsLoading(false)
        }
      }
      initializeAuth()
      return
    }

    // Config is loaded. In production MSAL mode wait for MSAL to finish any
    // in-progress interaction (login, acquireToken, handleRedirect, …) before
    // checking auth, to avoid calling acquireTokenSilent concurrently.
    if (!isDevelopmentAuth(runtimeConfig) && inProgress && inProgress !== 'none') {
      return
    }

    if (isDevelopmentAuth(runtimeConfig)) {
      checkDevAuthStatus()
    } else {
      checkMsalAuthStatus()
    }
  }, [accounts, inProgress, runtimeConfig])

  // Listen for AUTH_TOKEN messages from parent app (iframe context)
  useEffect(() => {
    if (!isInIframe()) return

    const handleParentMessage = async (event) => {
      if (event.data?.type !== 'AUTH_TOKEN' || !event.data?.accessToken) {
        return
      }

      if (parentTokenTimeoutRef.current) {
        clearTimeout(parentTokenTimeoutRef.current)
        parentTokenTimeoutRef.current = null
      }

      sessionStorage.setItem(PARENT_TOKEN_KEY, event.data.accessToken)
      localStorage.setItem(PREAUTH_CACHE_KEY, JSON.stringify({
        token: event.data.accessToken,
        expiry: Date.now() + PREAUTH_CACHE_TTL_MS,
      }))

      try {
        const validateResponse = await authAPI.validateToken()

        if (!validateResponse.has_required_roles) {
          console.warn('User does not have required roles')
          notifyAuthFailed('User does not have required roles')
          sessionStorage.removeItem(PARENT_TOKEN_KEY)
          setIsAuthenticated(false)
          setUserAndPersist(null)
          setAuthError('User does not have required roles')
          setIsLoading(false)
          return
        }

        const completeUser = {
          email: validateResponse?.user?.email || 'unknown',
          name: validateResponse?.user?.name || validateResponse?.user?.email || 'Unknown User',
          roles: validateResponse?.user?.roles || [],
          application_roles: validateResponse?.user?.application_roles || [],
          preferred_language: validateResponse?.user?.preferred_language || 'en',
        }

        setUserAndPersist(completeUser)
        setIsAuthenticated(true)
        setAuthError(null)
        setIsLoading(false)
        hasValidated.current = true
        isValidating.current = false
        notifyAuthSuccess({ username: completeUser.email, name: completeUser.name })
      } catch (error) {
        console.error('Failed to validate parent token:', error)
        sessionStorage.removeItem(PARENT_TOKEN_KEY)
        notifyAuthFailed(error.message || 'Token validation failed')
        setIsAuthenticated(false)
        setUserAndPersist(null)
        setAuthError(error.message || 'Token validation failed')
        setIsLoading(false)
      }
    }

    window.addEventListener('message', handleParentMessage)
    return () => {
      window.removeEventListener('message', handleParentMessage)
      if (parentTokenTimeoutRef.current) {
        clearTimeout(parentTokenTimeoutRef.current)
        parentTokenTimeoutRef.current = null
      }
    }
  }, [])

  const checkDevAuthStatus = async () => {
    if (user && user.application_roles !== undefined) {
      setIsLoading(false)
      return
    }

    if (isValidating.current || hasValidated.current) {
      if (hasValidated.current) {
        setIsLoading(false)
      }
      return
    }

    const token = localStorage.getItem('agentflow_jwt')

    if (!token) {
      setIsAuthenticated(false)
      setIsLoading(false)
      hasValidated.current = false
      isValidating.current = false
      return
    }

    isValidating.current = true

    try {
      const userInfo = authAPI.parseJWT(token)
      const validateResponse = await authAPI.validateToken()

      if (!validateResponse.has_required_roles) {
        console.warn('User does not have required roles')
        localStorage.removeItem('agentflow_jwt')
        setIsAuthenticated(false)
        setUserAndPersist(null)
        hasValidated.current = false
        isValidating.current = false
        setIsLoading(false)
        localStorage.setItem('agentflow_access_denied', "You don't have permission to access this application.")
        return
      }

      if (userInfo) {
        const completeUser = {
          email: userInfo.email,
          name: userInfo.name || userInfo.email,
          roles: userInfo.roles || [],
          application_roles: validateResponse?.user?.application_roles || [],
          preferred_language: validateResponse?.user?.preferred_language || 'en',
        }

        setUserAndPersist(completeUser)
        setIsAuthenticated(true)
        setIsLoading(false)
        hasValidated.current = true
        isValidating.current = false
      } else {
        console.error('[Auth Debug] Failed to parse userInfo from JWT')
        localStorage.removeItem('agentflow_jwt')
        setIsAuthenticated(false)
        setUserAndPersist(null)
        hasValidated.current = false
        isValidating.current = false
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Authentication error:', error)
      localStorage.removeItem('agentflow_jwt')
      setIsAuthenticated(false)
      setUserAndPersist(null)
      hasValidated.current = false
      isValidating.current = false
      setIsLoading(false)
    }
  }

  const checkMsalAuthStatus = async () => {
    if (!shouldUseMsal) {
      setIsLoading(false)
      return
    }

    if (user && user.application_roles !== undefined) {
      setIsLoading(false)
      return
    }

    if (isValidating.current || hasValidated.current) {
      if (hasValidated.current) {
        setIsLoading(false)
      }
      return
    }

    if (inProgress === 'none' && accounts && accounts.length > 0) {
      isValidating.current = true

      try {
        const account = accounts[0]
        const validateResponse = await authAPI.validateToken()

        if (!validateResponse.has_required_roles) {
          console.warn('User does not have required roles')
          setIsAuthenticated(false)
          setUserAndPersist(null)
          hasValidated.current = false
          isValidating.current = false
          setIsLoading(false)
          await logout()
          return
        }

        const completeUser = {
          email: account.username,
          name: account.name,
          roles: validateResponse?.user?.roles || [],
          application_roles: validateResponse?.user?.application_roles || [],
          preferred_language: validateResponse?.user?.preferred_language || 'en',
        }

        setUserAndPersist(completeUser)
        setIsAuthenticated(true)
        setIsLoading(false)
        hasValidated.current = true
        isValidating.current = false
        notifyAuthSuccess(account)
      } catch (error) {
        console.error('Failed to authenticate with MSAL:', error)
        setIsAuthenticated(false)
        setUserAndPersist(null)
        hasValidated.current = false
        isValidating.current = false
        setIsLoading(false)
        notifyAuthFailed(error.message || 'Authentication failed')
      }
    } else if (inProgress === 'none') {
      // No user signed in - try SSO silent with session hints (for iframe context)
      isValidating.current = true

      try {
        const urlParams = new URLSearchParams(window.location.search)
        const loginHint = urlParams.get('login_hint')
        const domainHint = urlParams.get('domain_hint')
        const sid = urlParams.get('sid')

        const loginRequest = createLoginRequest(runtimeConfig)
        const ssoRequest = {
          ...loginRequest,
          ...(loginHint && { loginHint }),
          ...(domainHint && { domainHint }),
          ...(sid && { sid }),
        }

        const response = await instance.ssoSilent(ssoRequest)
        instance.setActiveAccount(response.account)

        const validateResponse = await authAPI.validateToken()

        if (!validateResponse.has_required_roles) {
          console.warn('User does not have required roles')
          notifyAuthFailed('User does not have required roles')
          setIsAuthenticated(false)
          setUserAndPersist(null)
          hasValidated.current = false
          isValidating.current = false
          setIsLoading(false)
          return
        }

        const completeUser = {
          email: response.account.username,
          name: response.account.name,
          roles: validateResponse?.user?.roles || [],
          application_roles: validateResponse?.user?.application_roles || [],
          preferred_language: validateResponse?.user?.preferred_language || 'en',
        }

        setUserAndPersist(completeUser)
        setIsAuthenticated(true)
        setIsLoading(false)
        hasValidated.current = true
        isValidating.current = false
        notifyAuthSuccess(response.account)
      } catch (silentError) {
        if (isInIframe()) {
          // Check if a pre-auth iframe already cached a valid token in localStorage
          try {
            const cached = JSON.parse(localStorage.getItem(PREAUTH_CACHE_KEY) || 'null')
            if (cached && cached.token && Date.now() < cached.expiry) {
              sessionStorage.setItem(PARENT_TOKEN_KEY, cached.token)
              const validateResponse = await authAPI.validateToken()
              if (validateResponse.has_required_roles) {
                const completeUser = {
                  email: validateResponse?.user?.email || 'unknown',
                  name: validateResponse?.user?.name || validateResponse?.user?.email || 'Unknown User',
                  roles: validateResponse?.user?.roles || [],
                  application_roles: validateResponse?.user?.application_roles || [],
                  preferred_language: validateResponse?.user?.preferred_language || 'en',
                }
                setUserAndPersist(completeUser)
                setIsAuthenticated(true)
                setIsLoading(false)
                hasValidated.current = true
                isValidating.current = false
                notifyAuthSuccess({ username: completeUser.email, name: completeUser.name })
                return
              }
            }
          } catch {
            localStorage.removeItem(PREAUTH_CACHE_KEY)
          }

          notifyAuthRequired()

          parentTokenTimeoutRef.current = setTimeout(() => {
            console.warn('Parent app did not provide auth token within timeout')
            setIsAuthenticated(false)
            setUserAndPersist(null)
            setAuthError('Authentication timeout. The parent application did not provide credentials.')
            hasValidated.current = false
            isValidating.current = false
            setIsLoading(false)
            notifyAuthFailed('Authentication timeout')
          }, PARENT_AUTH_TIMEOUT_MS)
        } else {
          setIsAuthenticated(false)
          setUserAndPersist(null)
          hasValidated.current = false
          isValidating.current = false
          setIsLoading(false)
        }
      }
    }
  }

  const getAccessToken = async () => {
    const hasMsalAuth = shouldUseMsal && accounts && accounts.length > 0
    if (!hasMsalAuth) {
      const parentToken = sessionStorage.getItem(PARENT_TOKEN_KEY)
      if (parentToken) {
        return parentToken
      }
    }

    if (!runtimeConfig) {
      throw new Error('Runtime config not loaded')
    }

    if (isDevelopmentAuth(runtimeConfig)) {
      return localStorage.getItem('agentflow_jwt')
    }

    const activeAccount = accounts?.[0] || instance?.getActiveAccount()
    if (!shouldUseMsal || !activeAccount) {
      throw new Error('No authenticated user')
    }

    const tokenRequest = createTokenRequest(runtimeConfig)
    const request = {
      ...tokenRequest,
      account: activeAccount,
    }

    try {
      const response = await instance.acquireTokenSilent(request)
      return response.accessToken
    } catch (error) {
      // In an iframe, MSAL silent refresh is blocked by the sandbox (no allow-top-navigation).
      // For any MSAL error in iframe context, fall back to the parent token flow.
      if (isInIframe()) {
        const parentToken = sessionStorage.getItem(PARENT_TOKEN_KEY)
        if (parentToken) {
          return parentToken
        }
        console.log('[AgentFlow] MSAL acquireTokenSilent failed in iframe, requesting token from parent:', error.message)
        notifyAuthRequired()
        throw new Error('Token expired. Requesting new token from parent application.')
      }

      if (error instanceof InteractionRequiredAuthError) {
        try {
          await instance.acquireTokenRedirect(request)
          return null // Won't reach here due to redirect
        } catch (redirectError) {
          console.warn('Redirect failed, falling back to popup:', redirectError)
          const response = await instance.acquireTokenPopup(request)
          return response.accessToken
        }
      }
      throw error
    }
  }

  const login = async (manualToken = null, usePopup = false) => {
    if (!runtimeConfig) {
      throw new Error('Runtime config not loaded')
    }

    if (isDevelopmentAuth(runtimeConfig) && manualToken) {
      try {
        localStorage.setItem('agentflow_jwt', manualToken)
        const userInfo = authAPI.parseJWT(manualToken)
        const validateResponse = await authAPI.validateToken()

        if (userInfo) {
          const completeUser = {
            email: userInfo.email,
            name: userInfo.name || userInfo.email,
            roles: userInfo.roles || [],
            application_roles: validateResponse?.user?.application_roles || [],
            preferred_language: validateResponse?.user?.preferred_language || 'en',
          }
          setUserAndPersist(completeUser)
        }

        setIsAuthenticated(true)
        return true
      } catch (error) {
        console.error('Login failed:', error)
        localStorage.removeItem('agentflow_jwt')
        setUserAndPersist(null)
        throw error
      }
    } else if (!isDevelopmentAuth(runtimeConfig) && instance) {
      try {
        const loginRequest = createLoginRequest(runtimeConfig)

        if (isInIframe()) {
          notifyAuthRequired()
          parentTokenTimeoutRef.current = setTimeout(() => {
            console.warn('Parent app did not provide auth token within timeout')
            setAuthError('Authentication timeout. The parent application did not provide credentials.')
            notifyAuthFailed('Authentication timeout')
          }, PARENT_AUTH_TIMEOUT_MS)
          return true
        } else if (usePopup) {
          const response = await instance.loginPopup(loginRequest)
          notifyAuthSuccess(response.account)
        } else {
          await instance.loginRedirect(loginRequest)
          // Note: This will redirect the user away from the app
        }

        return true
      } catch (error) {
        console.error('Login failed:', error)
        if (isInIframe()) {
          notifyAuthFailed(error.message || 'Login failed')
        }
        throw error
      }
    }
  }

  const logout = async (usePopup = false) => {
    if (!runtimeConfig) {
      throw new Error('Runtime config not loaded')
    }

    hasValidated.current = false
    setIsAuthenticated(false)
    setUserAndPersist(null)

    if (isDevelopmentAuth(runtimeConfig)) {
      localStorage.removeItem('agentflow_jwt')
    } else if (instance && accounts && accounts.length > 0) {
      const logoutRequest = { account: accounts[0] }

      if (usePopup) {
        await instance.logoutPopup(logoutRequest)
      } else {
        await instance.logoutRedirect(logoutRequest)
      }
    }
  }

  const patchUser = (fields) => {
    setUserAndPersist({ ...user, ...fields })
  }

  // Called when the API rejects the token (401) and it can't be recovered.
  // Clears the stale session — including the persisted sessionStorage user —
  // so the app drops back to the login flow and rebuilds the user (and roles)
  // from server validation on the next sign-in. Unlike logout(), this does not
  // trigger an MSAL logoutRedirect, so it works even when the MSAL cache is gone.
  const handleAuthExpired = () => {
    hasValidated.current = false
    setIsAuthenticated(false)
    setUserAndPersist(null)
    // Clear the dev-auth JWT too, for parity with logout() — otherwise a stale
    // token would keep 401-ing on each reload in development auth mode.
    localStorage.removeItem('agentflow_jwt')
    // LoginPage surfaces this on mount so the user knows why they're back at login.
    localStorage.setItem('agentflow_access_denied', 'Your session has expired. Reconnecting automatically...')
  }

  const value = {
    isAuthenticated,
    isLoading,
    user,
    authError,
    login,
    logout,
    handleAuthExpired,
    getAccessToken,
    patchUser,
    isDevelopmentAuth: runtimeConfig ? isDevelopmentAuth(runtimeConfig) : false,
    runtimeConfig,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
